import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import clsx from 'clsx';
import * as Yup from 'yup';
import axios from 'axios';
import { toast } from 'react-toastify';

const WALLET_API = process.env.REACT_APP_API?.replace(/\/api\/v1\/$/, '') || '';

const confirmPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, 'La contraseña debe tener al menos 8 caracteres')
    .required('Contraseña es requerida.'),
  confirmPassword: Yup.string()
    .required('Confirmar contraseña es requerida.')
    .oneOf([Yup.ref('password'), null], 'Las contraseñas no coinciden'),
});

const initialValues = {
  password: '',
  confirmPassword: '',
};

export function WalletConfirmPassword() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  let { token }: { token: string } = useParams();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const formik = useFormik({
    initialValues,
    validationSchema: confirmPasswordSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      try {
        await axios.post(
          `${WALLET_API}/api/v1/wallet/reset/${token}`,
          {
            password: values.password,
            confirm_password: values.confirmPassword,
          }
        );
        setSuccess(true);
        toast.success('¡Contraseña restablecida correctamente! Ya puedes iniciar sesión en la App.', {
          position: 'top-right',
          autoClose: 5000,
        });
      } catch (err: any) {
        const message =
          err?.response?.data?.message || 'El enlace es inválido o ha expirado.';
        setStatus(message);
        toast.error(message, { position: 'top-right', autoClose: 4000 });
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  if (success) {
    return (
      <div className="text-center">
        <div className="mb-6">
          <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
        </div>
        <h2 className="text-dark mb-4">¡Contraseña restablecida!</h2>
        <p className="text-muted fs-6">
          Tu contraseña ha sido actualizada correctamente.
          <br />
          Ya puedes iniciar sesión en la App Wallet con tu nueva contraseña.
        </p>
      </div>
    );
  }

  return (
    <form
      className="form w-100"
      onSubmit={formik.handleSubmit}
      noValidate
      id="kt_wallet_confirm_form"
    >
      <div className="text-center mb-10">
        <h1 className="text-dark mb-3">Restablecer contraseña</h1>
      </div>

      {formik.status && (
        <div className="mb-lg-15 alert alert-danger">
          <div className="alert-text font-weight-bold">{formik.status}</div>
        </div>
      )}

      {/* Contraseña */}
      <div className="fv-row mb-10">
        <div className="d-flex flex-stack mb-2">
          <label className="form-label fw-bolder text-dark fs-6 mb-0">Contraseña</label>
        </div>
        <div className={clsx('position-relative')}>
          <input
            type={showPassword ? 'text' : 'password'}
            autoComplete="off"
            {...formik.getFieldProps('password')}
            className={clsx('form-control form-control-lg')}
          />
          <span
            className={clsx('btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-n2')}
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <i className={clsx('bi bi-eye fs-2')}></i>
            ) : (
              <i className={clsx('bi bi-eye-slash fs-2')}></i>
            )}
          </span>
        </div>
        {formik.touched.password && formik.errors.password && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block text-danger mt-3">
              <span role="alert">{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>

      {/* Confirmar Contraseña */}
      <div className="fv-row mb-10">
        <div className="d-flex flex-stack mb-2">
          <label className="form-label fw-bolder text-dark fs-6 mb-0">Confirmar contraseña</label>
        </div>
        <div className={clsx('position-relative')}>
          <input
            type={showConfirm ? 'text' : 'password'}
            autoComplete="off"
            {...formik.getFieldProps('confirmPassword')}
            className={clsx('form-control form-control-lg')}
          />
          <span
            className={clsx('btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-n2')}
            onClick={() => setShowConfirm(!showConfirm)}
          >
            {showConfirm ? (
              <i className={clsx('bi bi-eye fs-2')}></i>
            ) : (
              <i className={clsx('bi bi-eye-slash fs-2')}></i>
            )}
          </span>
        </div>
        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block text-danger mt-3">
              <span role="alert">{formik.errors.confirmPassword}</span>
            </div>
          </div>
        )}
        <div className="form-text mb-5">
          La contraseña debe ser al menos de 8 caracteres, contener mayúscula, minúsculas, números y algún signo [@#$%^&-+=()]]
        </div>
      </div>

      <div className="text-center">
        <button
          type="submit"
          id="kt_wallet_confirm_submit"
          className="btn btn-lg btn-primary w-100 mb-5"
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className="indicator-label">Restablecer</span>}
          {loading && (
            <span className="indicator-progress" style={{ display: 'block' }}>
              Espere por favor...
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
      </div>
    </form>
  );
}
